import useMediaQuery from '@/hooks/useMediaQuery';
import useServiceType from '@/hooks/useServiceType';
import useSetLoading from '@/hooks/useSetLoading';
import apiNuroSupportPage from '@/nuroSupportPage/utils/apiNuroSupportPage';
import { ApiUrlNuroSupportPage } from '@/nuroSupportPage/utils/constants';
import { LoginInfoState } from '@/states/loginInfoState';
import { userState } from '@/states/selectors/userState';
import {
  serviceIdState,
  serviceInfoState,
  serviceLinkState,
  serviceMobileSimPhoneNumberState,
} from '@/states/serviceInfoState';
import { getCredentials, getSigV4HeadersForGet } from '@/utils/cognito';
import { checkIsLogin, validateRedirectUri } from '@/utils/common';
import { formatPhoneNumber } from '@/utils/commonFunc';
import { ApiUrl, Constants } from '@/utils/constants';
import axios from 'axios';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useId, useRef, useState } from 'react';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import url from '../../utils/config';

/**
 * 共通ヘッダー。
 * ログイン状態やサービス種別、現在の導線に応じて表示内容を切り替え、
 * ハンバーガーメニューの開閉とログアウト導線もここで管理する。
 */
const Header = () => {
  const serviceType = useServiceType();

  // ハンバーガーメニューの開閉状態
  const [isShowHamburger, setIsShowHamburger] = useState(false);
  // レスポンシブ用のメディアクエリ
  const isCompact = useMediaQuery(640);
  const user = useRecoilValue(userState);
  const resetLoginInfo = useResetRecoilState(LoginInfoState);
  const { startLoading, finishLoading } = useSetLoading();
  const router = useRouter();
  const [isLogin, setIsLogin] = useState(false);
  const loginInfo = useRecoilValue(LoginInfoState);
  const simPhoneNumber = useRecoilValue(serviceMobileSimPhoneNumberState);
  const serviceLink = useRecoilValue(serviceLinkState);
  const serviceInfo = useRecoilValue(serviceInfoState);
  const serviceId = useRecoilValue(serviceIdState);

  useEffect(() => {
    // ページ遷移時にハンバーガーメニューを閉じる
    setIsShowHamburger(false);

    // 表示判定
    if (checkIsLogin(router.pathname)) {
      setIsLogin(true);
    } else {
      setIsLogin(false);
    }

    // 戸建プランでメールアドレスの登録がない場合は、メールアドレス登録画面でヘッダフッタを表示しない
    // リダイレクト先URLが設定されている場合は、ヘッダフッタを表示しない
    const validatedRedirectUri = validateRedirectUri(router.asPath);
    if (
      router.pathname.startsWith('/contract/mail') &&
      ((user.isNuro && !loginInfo.is_verified_email) || validatedRedirectUri !== '')
    ) {
      setIsLogin(false);
    }

    // サービス選択画面に遷移する場合は、ヘッダを表示しない
    if (router.pathname.startsWith('/service')) {
      setIsLogin(false);
    }

    // MNPワンストップ転出画面に遷移する場合は、ヘッダを表示しない
    if (router.pathname.startsWith('/mobile/contract/quit/onestop')) {
      setIsLogin(false);
    }

    // NUROモバイルでパスワードが初期パスワードのままの場合は、パスワード変更画面でヘッダを表示しない
    const thisServices = serviceInfo.find(({ service_id }) => service_id === serviceId);
    if (
      router.pathname.startsWith('/mobile/contract/password') &&
      serviceType === Constants.ServiceType.MOBILE
    ) {
      if (
        thisServices &&
        thisServices.password_setter &&
        thisServices.password_setter === Constants.PasswordSetter.SYSTEM
      ) {
        setIsLogin(false);
      }
    }

    // ログイン必須ページなのに認証情報がなければ、ヘッダー表示前にログインへ戻す。
    if (
      !Constants.PathList.NOT_LOGIN.some((path) => router.pathname.startsWith(path)) &&
      getCredentials() === null
    ) {
      router.push('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.pathname]);

  // プランに合わせてオプション規約一覧のリンクを返す
  const getOptionLegalLink = (): string => {
    switch (user.planType) {
      // NURO 光 2G系
      case '2G':
        return 'https://www.nuro.jp/hikari/legal/';
      // forマンション系
      case 'mansion':
        return 'https://www.nuro.jp/mansion/legal/';
      // NURO 光 10G
      case '10G':
        return 'https://www.nuro.jp/10g/legal/';
      // NURO 光 G10T/G10/6Gs/20Gs & 新10ギガ系 (=XGS系)
      case '10Gs':
      case '6Gs':
      case '20Gs':
        return 'https://www.nuro.jp/s_plan/legal/';
      default:
        return '';
    }
  };

  const controlId = `js-menu-${useId()}`;
  const spTarget = useRef<HTMLDivElement>(null!);
  const hamburgerMenuRef = useRef<HTMLDivElement>(null);
  const hamburgerBtnRef = useRef<HTMLButtonElement>(null);
  const lastFocusedElement = useRef<HTMLElement | null>(null);

  /** ハンバーガーボタン押下時に開閉状態を切り替える。 */
  const handleClickHamburgerButton = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void => {
    event.preventDefault();
    if (!isShowHamburger) {
      // メニューを開く直前にフォーカスされていた要素を記録
      lastFocusedElement.current = document.activeElement as HTMLElement;
    }
    setIsShowHamburger((prev) => !prev);
  };
  // メニュー展開中はフォーカスをメニュー内に閉じ込め、キーボード操作でも扱えるようにする。
  useEffect(() => {
    if (!isShowHamburger) {
      // メニューを閉じたら元の要素にフォーカスを戻す
      if (lastFocusedElement.current) {
        lastFocusedElement.current.focus();
      }
      return;
    }
    const menu = hamburgerMenuRef.current;
    const btn = hamburgerBtnRef.current;
    if (!menu || !btn) return;
    // メニュー内のフォーカス可能要素を取得
    const focusableSelectors = [
      'a[href]:not([tabindex="-1"]):not([disabled])',
      'button:not([disabled]):not([tabindex="-1"])',
      'input:not([disabled]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      'textarea:not([disabled]):not([tabindex="-1"])',
      '[tabindex]:not([tabindex="-1"])',
    ];
    // メニューボタンも含める
    const menuFocusableEls = Array.from(
      menu.querySelectorAll<HTMLElement>(focusableSelectors.join(','))
    );
    // メニューボタンを先頭に追加
    menuFocusableEls.unshift(btn);
    // 最初の要素にフォーカス
    menuFocusableEls[0].focus();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const firstEl = menuFocusableEls[0];
      const lastEl = menuFocusableEls[menuFocusableEls.length - 1];
      if (e.shiftKey) {
        // Shift+Tab: 最初なら最後へ
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      }
      // Tab: 最後なら最初へ
      if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    // クリーンアップ関数
    // クリーンアップ（何も返さない）
  }, [isShowHamburger]);

  /** 認証 API とサポートページ API の両方でログアウト処理を行う。 */
  const handleClickLogOutButton = () => {
    (async () => {
      startLoading(ApiUrl.Authn.LOGOUT);
      const headers = await getSigV4HeadersForGet(ApiUrl.Authn.LOGOUT);
      await axios
        .get(`${process.env.NEXT_PUBLIC_AUTH_URL}${ApiUrl.Authn.LOGOUT}`, headers)
        .catch(() => {})
        .finally(() => {
          resetLoginInfo();
          setIsShowHamburger(false);
          logout();
          finishLoading(ApiUrl.Authn.LOGOUT);
          sessionStorage.clear();
          window.location.href = url('/login');
        });
    })();
  };

  /** NUROサポートページ側のセッションも破棄する。 */
  const logout = () => {
    const apiUrl = ApiUrlNuroSupportPage.LOGOUT;

    (async () => {
      await apiNuroSupportPage
        .post(
          apiUrl,
          { del_type: 'all' },
          {
            withCredentials: true,
          }
        )
        .catch(() => {
          // 特にエラーにはしない
        });
    })();
  };

  return (
    <header className='l-header'>
      <div className='l-header__banner'>
        <img
          className='l-header__bannerLogo'
          src={url('/images/header_logo-sony.png')}
          alt='SONY'
        />
      </div>
      {isLogin && (
        <div>
          <div className='l-header__mainArea'>
            <div className='l-header__inner'>
              <div className='l-header__wrap'>
                <div className='l-header__title'>
                  {serviceType === Constants.ServiceType.MOBILE ? (
                    <Link href='/mobile/top' legacyBehavior>
                      <img src={url('/images/logo_nuromobile.png')} alt='NUROモバイル' />
                    </Link>
                  ) : (
                    <Link href='/top' legacyBehavior>
                      <img src={url('/images/header_logo-nuro.png')} alt='NURO 光' />
                    </Link>
                  )}
                </div>
                {serviceType === Constants.ServiceType.MOBILE ? (
                  <div className='l-header__user'>{formatPhoneNumber(`${simPhoneNumber}`)}</div>
                ) : (
                  <div className='l-header__user'>{`${user.name} さま`}</div>
                )}
                <div className='l-header__hamburger'>
                  <button
                    id={controlId}
                    className={`l-header__hamburgerBtn js-header-hook ${
                      isShowHamburger ? 'is-hamburger-show' : ''
                    }`}
                    type='button'
                    onClick={handleClickHamburgerButton}
                    aria-expanded={isShowHamburger ? 'true' : 'false'}
                    aria-label={isShowHamburger ? 'メニューを閉じる' : 'メニューを開く'}
                    ref={hamburgerBtnRef}
                  >
                    <span className='l-header__hamburgerMenu_hook js-header-hook-text'>
                      {isShowHamburger ? 'メニューを閉じる' : 'メニューを開く'}
                    </span>
                  </button>
                </div>
              </div>
              {(isShowHamburger || !isCompact) && (
                <div
                  className={`l-header__menuWrap js-dropDown ${
                    isCompact ? 'is-hamburger-show' : ''
                  }`}
                  ref={spTarget}
                  aria-controls={isCompact ? controlId : ''}
                  role={isCompact ? 'dialog' : ''}
                >
                  {serviceType === Constants.ServiceType.MOBILE ? (
                    <div className='l-header__userBreaksp'>
                      {formatPhoneNumber(`${simPhoneNumber}`)}
                    </div>
                  ) : (
                    <div className='l-header__userBreaksp'>{`${user.name} さま`}</div>
                  )}
                  <nav className='l-header__nav' aria-label='グローバルナビゲーション'>
                    <ul className='l-header__navList'>
                      <li className='l-header__navItem'>
                        <Link
                          href={
                            serviceType === Constants.ServiceType.MOBILE ? '/mobile/top' : '/top'
                          }
                          className='l-header__navLink'
                          tabIndex={(() => {
                            if (isCompact) {
                              return isShowHamburger ? 0 : -1;
                            }
                            return isShowHamburger ? -1 : 0;
                          })()}
                        >
                          <img
                            className='l-header__navIcon'
                            src={url('/images/icon_home_white.svg')}
                            alt=''
                          />
                          料金・お知らせ
                        </Link>
                      </li>
                      <li className='l-header__navItem'>
                        <Link
                          href={
                            serviceType === Constants.ServiceType.MOBILE
                              ? '/mobile/contract'
                              : '/contract'
                          }
                          className='l-header__navLink'
                          tabIndex={(() => {
                            if (isCompact) {
                              return isShowHamburger ? 0 : -1;
                            }
                            return isShowHamburger ? -1 : 0;
                          })()}
                        >
                          <img
                            className='l-header__navIcon'
                            src={url('/images/icon_file_white.svg')}
                            alt=''
                          />
                          ご契約情報
                        </Link>
                      </li>
                      <li className='l-header__navItem'>
                        <Link
                          href={
                            serviceType === Constants.ServiceType.MOBILE
                              ? '/mobile/option'
                              : '/option'
                          }
                          className='l-header__navLink'
                          tabIndex={(() => {
                            if (isCompact) {
                              return isShowHamburger ? 0 : -1;
                            }
                            return isShowHamburger ? -1 : 0;
                          })()}
                        >
                          <img
                            className='l-header__navIcon'
                            src={url('/images/icon_gear_white.svg')}
                            alt=''
                          />
                          ご契約オプション
                        </Link>
                      </li>
                      <li className='l-header__navItem'>
                        <Link
                          href={
                            serviceType === Constants.ServiceType.MOBILE
                              ? '/mobile/campaign'
                              : '/campaign'
                          }
                          className='l-header__navLink'
                          tabIndex={(() => {
                            if (isCompact) {
                              return isShowHamburger ? 0 : -1;
                            }
                            return isShowHamburger ? -1 : 0;
                          })()}
                        >
                          <img
                            className='l-header__navIcon'
                            src={url('/images/icon_present_white.svg')}
                            alt=''
                          />
                          特典・キャンペーン
                        </Link>
                      </li>
                    </ul>
                  </nav>
                  {isShowHamburger && (
                    <div
                      className={`l-hamburger js-hamburger ${isCompact ? '' : 'is-hamburger-show'}`}
                      ref={hamburgerMenuRef}
                      aria-controls={isCompact ? '' : controlId}
                      role={isCompact ? '' : 'dialog'}
                    >
                      <div className='l-hamburger__inner' tabIndex={-1}>
                        <dl className='l-hamburger__menu'>
                          <dt className='l-hamburger__menuList'>障害・メンテナンス情報</dt>
                          <dd className='l-hamburger__menuItem'>
                            {serviceType === Constants.ServiceType.MOBILE ? (
                              <a
                                className='l-hamburger__menuItemLink'
                                href='https://www.nuro.jp/emerge/'
                                target='_blank'
                                rel='noreferrer'
                              >
                                NUROモバイル 障害・メンテナンス情報
                              </a>
                            ) : (
                              <a
                                className='l-hamburger__menuItemLink'
                                href='https://www.nuro.jp/emerge/'
                                target='_blank'
                                rel='noreferrer'
                              >
                                NURO 光 障害・メンテナンス情報
                              </a>
                            )}
                          </dd>
                          <dd className='l-hamburger__menuItem'>
                            <a
                              className='l-hamburger__menuItemLink'
                              href='https://www.sonynetwork.co.jp/corporation/safety/'
                              target='_blank'
                              rel='noreferrer'
                            >
                              個人情報保護 / 情報セキュリティ
                            </a>
                          </dd>
                          <dd className='l-hamburger__menuItem'>
                            <a
                              className='l-hamburger__menuItemLink'
                              href='https://www.nuro.jp/siteinfo/nuro/'
                              target='_blank'
                              rel='noreferrer'
                            >
                              ウェブサイトご利用条件
                            </a>
                          </dd>
                        </dl>
                        {serviceType === Constants.ServiceType.HIKARI && (
                          <>
                            <dl className='l-hamburger__menu'>
                              <dt className='l-hamburger__menuList'>サポート</dt>
                              <dd className='l-hamburger__menuItem'>
                                <a
                                  className='l-hamburger__menuItemLink'
                                  href='https://www.nuro.jp/support/'
                                  target='_blank'
                                  rel='noreferrer'
                                >
                                  NURO 光 会員サポート
                                </a>
                              </dd>
                            </dl>
                            <dl className='l-hamburger__menu'>
                              <dt className='l-hamburger__menuList'>規約</dt>
                              <dd className='l-hamburger__menuItem'>
                                <a
                                  className='l-hamburger__menuItemLink'
                                  href='https://www.nuro.jp/hikari/legal/honsoku/kiyaku.pdf'
                                  target='_blank'
                                  rel='noreferrer'
                                >
                                  NURO会員規約
                                </a>
                              </dd>
                              <dd className='l-hamburger__menuItem'>
                                <a
                                  className='l-hamburger__menuItemLink'
                                  href={getOptionLegalLink()}
                                  target='_blank'
                                  rel='noreferrer'
                                >
                                  オプションサービスの規約一覧
                                </a>
                              </dd>
                            </dl>
                          </>
                        )}
                        <dl className='l-hamburger__menu'>
                          <dt className='l-hamburger__menuList'>セキュリティ</dt>
                          <dd className='l-hamburger__menuItem'>
                            <Link
                              href={`${serviceType === 'mobile' ? '/mobile' : ''}/login_history`}
                              className='l-hamburger__menuItemLink'
                              rel='noreferrer'
                            >
                              ログイン履歴
                            </Link>
                          </dd>
                        </dl>
                        {!serviceLink && (
                          <dl className='l-hamburger__menu'>
                            <dt className='l-hamburger__menuList'>
                              <Link
                                href='/service/'
                                className='l-hamburger__menuItemLink'
                                rel='noreferrer'
                              >
                                サービス選択
                              </Link>
                            </dt>
                          </dl>
                        )}
                        <button
                          className='l-hamburger__btn'
                          type='button'
                          onClick={handleClickLogOutButton}
                        >
                          <span className='l-hamburger__btnTxt'>ログアウト</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {/* <Caution /> */}
        </div>
      )}
    </header>
  );
};

export default Header;
